import {
  createFrontendPlugin,
  ApiBlueprint,
  createApiFactory,
  createApiRef,
  discoveryApiRef,
  configApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { PrometheusApi } from './PrometheusApi';

/**
 * New-frontend-system wrapper for the Roadie Prometheus plugin.
 *
 * We bypass Roadie's EntityPrometheusContent (a createRoutableExtension whose
 * routeRef the new system never registers) and render our own content, which
 * builds its queries from the entity instead of requiring annotations. The
 * compatWrapper bridge is still needed because Roadie's components read the
 * entity from the legacy catalog-react context. The Prometheus API factory is
 * registered via ApiBlueprint so useApi(prometheusApiRef) resolves.
 */

const prometheusApiRef = createApiRef<PrometheusApi>({
  id: 'plugin.prometheus.service',
});

const prometheusApi = ApiBlueprint.make({
  name: 'prometheus-api',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: prometheusApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          configApi: configApiRef,
          fetchApi: fetchApiRef,
        },
        factory: deps => new PrometheusApi(deps),
      }),
    ),
});

const prometheusEntityContent = EntityContentBlueprint.make({
  name: 'content',
  params: {
    path: '/prometheus',
    title: 'Prometheus',
    filter: 'kind:component',
    loader: async () => {
      const { compatWrapper } = await import('@backstage/core-compat-api');
      const { ServiceMetricsContent } = await import('./ServiceMetricsContent');
      return compatWrapper(<ServiceMetricsContent />);
    },
  },
});

export default createFrontendPlugin({
  pluginId: 'prometheus',
  extensions: [prometheusApi, prometheusEntityContent],
});
